import { Button } from '@/components/form/Button';
import { ToggleInput } from '@/components/form/fields/toggle/Toggle';
import { useGetNotificationEvents, useSyncNotificationPreferences } from '@/services/api/hooks/adminNotifications';
import { useCompanyId, usePartnerId, useUser, RoleType } from '@/services/authentication/hooks/user';
import { useCompany } from '@/services/api/hooks/companies';
import { usePartner } from '@/services/api/hooks/partners';
import { Navigate, useParams } from 'react-router';
import { FC, useEffect, useState } from 'react';

type PreferenceState = Record<string, { isEnabled: boolean; targetEmails: string; targetWebhooks: string }>;

const NotificationPreferences: FC = () => {
  const [events] = useGetNotificationEvents();
  const user = useUser();
  const [preferences, setPreferences] = useState<PreferenceState>({});

  const { mutateAsync: syncPreferences } = useSyncNotificationPreferences();
  const { partnerId: partnerIdParam, companyId: companyIdParam } = useParams<{ partnerId?: string; companyId?: string }>();
  
  const roleCompanyId = useCompanyId();
  const rolePartnerId = usePartnerId();

  const companyId = companyIdParam || roleCompanyId;
  const partnerId = partnerIdParam || rolePartnerId;

  const [company] = useCompany(
    user.role.type === RoleType.Company ? partnerId : undefined, 
    user.role.type === RoleType.Company ? companyId : undefined
  );
  const [partner] = usePartner(
    user.role.type === RoleType.Partner ? partnerId : undefined
  );


  // Initialize state with default empty values when events load
  useEffect(() => {
    if (events && events.length > 0 && Object.keys(preferences).length === 0) {
      const initial: PreferenceState = {};
      events.forEach(evt => {
        initial[evt.id] = { isEnabled: false, targetEmails: '', targetWebhooks: '' };
      });
      setPreferences(initial);
    }
  }, [events, preferences]);

  const handleToggle = (eventId: string, isEnabled: boolean) => {
    setPreferences(prev => {
      const current = prev[eventId] || { isEnabled: false, targetEmails: '', targetWebhooks: '' };
      return {
        ...prev,
        [eventId]: {
          ...current,
          isEnabled,
          // Pre-fill user email if turning on and currently empty
          targetEmails: isEnabled && !current.targetEmails ? user.email : current.targetEmails
        }
      };
    });
  };

  const handleEmailChange = (eventId: string, emails: string) => {
    setPreferences(prev => {
      const current = prev[eventId] || { isEnabled: false, targetEmails: '', targetWebhooks: '' };
      return {
        ...prev,
        [eventId]: {
          ...current,
          targetEmails: emails
        }
      };
    });
  };

  const handleWebhookChange = (eventId: string, webhooks: string) => {
    setPreferences(prev => {
      const current = prev[eventId] || { isEnabled: false, targetEmails: '', targetWebhooks: '' };
      return {
        ...prev,
        [eventId]: {
          ...current,
          targetWebhooks: webhooks
        }
      };
    });
  };

  const handleSave = async () => {
    // Validate webhooks
    for (const [_, data] of Object.entries(preferences)) {
      if (data.isEnabled && data.targetWebhooks) {
        const hooks = data.targetWebhooks.split(',').map(s => s.trim()).filter(Boolean);
        for (const url of hooks) {
          if (!url.startsWith('http://') && !url.startsWith('https://')) {
            alert(`Invalid Webhook URL: ${url}. Must start with http:// or https://`);
            return;
          }
        }
      }
    }

    const payload = Object.entries(preferences).map(([eventId, data]) => ({
      eventId,
      targetEmails: data.targetEmails.split(',').map(s => s.trim()).filter(Boolean),
      targetWebhooks: data.targetWebhooks.split(',').map(s => s.trim()).filter(Boolean),
      isEnabled: data.isEnabled
    }));
    
    try {
      await syncPreferences({
        scopeCompanyId: companyId,
        scopeMspId: partnerId,
        preferences: payload
      });
      alert('Preferences saved successfully!');
    } catch (e) {
      console.error(e);
      alert('Failed to save preferences.');
    }
  };

  // Route Guard: Ensure feature is enabled
  if (user.role.type === RoleType.Company && company && !company.enableCustomNotifications) {
    return <Navigate to="/" replace />;
  }
  if (user.role.type === RoleType.Partner && partner && !partner.enableCustomNotifications) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="space-y-8 max-w-4xl mx-auto py-8">
      <div>
        <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:truncate sm:text-3xl sm:tracking-tight">
          Notification Preferences
        </h2>
        <p className="mt-2 text-sm text-gray-500">
          Manage your subscriptions to system events. Enter comma-separated email addresses for delivery.
        </p>
      </div>

      <div className="bg-white shadow sm:rounded-lg overflow-hidden border border-gray-100">
        <ul role="list" className="divide-y divide-gray-200">
          {events?.map(event => {
            const pref = preferences[event.id] || { isEnabled: false, targetEmails: '', targetWebhooks: '' };

            return (
              <li key={event.id} className="p-6">
                <div className="flex items-start justify-between">
                  <div className="pr-4">
                    <h3 className="text-lg font-medium text-gray-900">{event.name}</h3>
                    <p className="mt-1 text-sm text-gray-500">{event.description}</p>
                  </div>
                  <div className="flex flex-col items-end space-y-4 min-w-[300px]">
                    <div className="flex items-center space-x-3">
                      <span className="text-sm font-medium text-gray-700">
                        {pref.isEnabled ? 'Enabled' : 'Disabled'}
                      </span>
                      <ToggleInput
                        id={`toggle-${event.id}`}
                        value={pref.isEnabled}
                        onChange={val => handleToggle(event.id, val)}
                      />
                    </div>
                    {pref.isEnabled && (
                      <div className="w-full space-y-4 mt-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Delivery Email Addresses
                          </label>
                          <input
                            type="text"
                            value={pref.targetEmails}
                            onChange={e => handleEmailChange(event.id, e.target.value)}
                            placeholder="admin@example.com, sales@example.com"
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Webhook URLs (comma separated)
                          </label>
                          <input
                            type="text"
                            value={pref.targetWebhooks}
                            onChange={e => handleWebhookChange(event.id, e.target.value)}
                            placeholder="https://api.example.com/webhook"
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
          {events?.length === 0 && (
            <li className="p-6 text-center text-gray-500">No events available.</li>
          )}
        </ul>
      </div>

      <div className="flex justify-end pt-4">
        <Button onClick={handleSave}>Save Preferences</Button>
      </div>
    </div>
  );
};

export default NotificationPreferences;
